<template lang="pug">
	.campaign-budget.flex-col
		Loader(v-if="isLoading", :fullscreen="true")
		table.budget-table(v-else, cellspacing="0")
			thead
				tr.header-row
					td
					template(v-for="(budget, idx) in campaignBudgets")
						td.budget-td
							.flex-row.justify
								EditableLabel(ref="budgetNameLabel", :editing="!budget.Name", :disabled="info.readonly")
									.name {{budget.Name}}
									template(slot="editor")
										DxTextBox(:value.sync="budget.Name", @value-changed="$refs.budgetNameLabel[0].release()")
								.flex-row(v-if="!info.readonly")
									.adt-button.clickable.flex-row.center.align-center.rounded.padded-lg(v-if="campaignBudgets.length > 1", @click="removeBudget(budget)", :title="$tCaption('Remove budget')")
										fas(icon="trash", size="2xs")
									.adt-button.clickable.flex-row.center.align-center.rounded.padded-lg(v-if="idx === campaignBudgets.length - 1", @click="addNewBudget()", :title="$tCaption('Add budget')")
										fas(icon="plus", size="2xs")
			tbody
				tr.details
					td.rounded-left
						.flex-row
							.font-small.upper.grey {{$tCaption('Products')}}
					template(v-for="budget in campaignBudgets")
						td.budget-td
							.flex-row.justify-end
								.font-small.upper.grey {{$tCaption('Term')}}
								.flex-row
									DxNumberBox.center-aligned-number-box(:value.sync="budget.NumberOfPeriods", format="#0", :width="40", @value-changed="onTermChange(budget)", :disabled="info.readonly")
									DxSelectBox(:items="periodTypes", :search-enabled="true", :value.sync="budget.BudgetPeriod", value-expr="id", display-expr="name", :width="90", :dropDownOptions="{ minWidth: 120 }", 
										@value-changed="onTermChange(budet)", :disabled="info.readonly")
				template(v-for="product in info.campaignProducts")
					CampaignBudgetProduct.simplified-editors(dataType="total", :info="info", :campaignBudgets="campaignBudgets", :budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", 
						:expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
						span(v-if="product.type.components.airtime", slot="total") {{$Data.formatNumber(product.AirtimeCost)}}
					template(v-if="expandedItems.indexOf(product.Oid) > -1")
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.budget", dataType="budget", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.display", dataType="display", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.geofencing", dataType="geofencing", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.remarketing", dataType="remarketing", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.airtime", :customName="$tCaption('Radio spots')", dataType="custom", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
							template(v-slot:custom="{ data }")
								.flex-row.flex-1.justify-end
									span {{$Data.formatNumber(product.AirtimeSpots)}}
						CampaignBudgetProduct.simplified-editors(v-if="product.type.components.airtime", :customName="$tCaption('Radio costs')", dataType="custom", :info="info", :campaignBudgets="campaignBudgets", 
							:budgetItems="budgetProductItems.find(f => f.product === product).items", :product="product", :expandedItems="expandedItems", :calcBudgetResolver="calcBudget")
							template(v-slot:custom="{ data }")
								.flex-row.flex-1.justify-end 
									span ${{$Data.formatNumber(product.AirtimeCost)}}
				tr.details.fees
					td
						.product.horizontal-spacing
							.icon
								i.fas.fa-dollar
							.name {{$tCaption('Fees')}}
					td.budget-td(v-for="budget in campaignBudgets")
						CampaignBudgetFee(:info="info", :budget="budget", :recurrencyPeriod="getReccurencyPeriod(budget)", :currencyFormat="currencyFormat", @change="calcBudget(budget)")
				tr.total-recurring-row.total-row.budget-totals
					td
						.product.horizontal-spacing
							.icon
								i.fas.fa-calculator
							.name {{$tCaption('Total recurring')}}
					template(v-for="budget in campaignBudgets")
						td.budget-td
							.budget-total.flex-row.justify
								.left
								.flex-row
									.value {{$Data.formatNumber(budget.TotalRecurring)}}
										.description.upper(v-if="budget.TotalRecurring") {{getReccurencyPeriod(budget)}}
				tr.grand-total-row.total-row.budget-totals
					td
						.product.horizontal-spacing
							.icon
								i.fas.fa-calculator
							.name {{$tCaption('Grand total')}}
					template(v-for="budget in campaignBudgets")
						td.budget-td
							.budget-total.flex-row.justify
								.left
								.flex-row
									.value {{$Data.formatNumber(budget.Total)}}
</template>

<script>
	import _ from 'lodash';
	import { DxNumberBox } from 'devextreme-vue/number-box';	
	import Localization from 'devextreme/localization.js';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import Loader from 'app-core/Loader.vue';
	import EditableLabel from 'app-core/EditableLabel.vue';
	import CampaignBudgetProduct from './CampaignBudgetProduct.vue';
	import CampaignBudgetFee from './CampaignBudgetFee.vue';

	export default {
		translationCategory: 'campaign.Budget',
		name: 'campaign-budget',
		components: {
			DxNumberBox,
			DxTextBox,
			DxSelectBox,
			Loader,
			EditableLabel,
			CampaignBudgetProduct,
			CampaignBudgetFee
		},
		props: ['info', 'activated'],
		data() {
			return {
				currencyFormat: '$#0.##',
				expandedItems: [],
				periodTypes: [{ id: 0, name: () => this.$tCaption('week(s)') }, { id: 1, name: () => this.$tCaption('month(s)') }],
				campaignBudgets: [],
				isLoading: 0
			};
		},
		computed: {
			campaign() {
				return this.info?.campaign;
			},
			budgetProductItems() {
				return this.info.campaignProducts.map(product => ({
					product,
					items: this.getBudgetItems(product)
				}));
			}
		},
		watch: {
			activated(v) {
				if (v && !this.campaignBudgets.length && !this.isLoading) {
					this.loadCampaignBudgets();
					if (this.info.wizard)
						this.expandedItems = this.info.campaignProducts.map(f => f.Oid);
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.campaignBudget = this;
			if (this.activated)
				this.loadCampaignBudgets();
		},
		methods: {
			onTermChange(budget) {
				this.calcBudget(budget);
			},
			calcBudget(budget, budgetProduct) {
				if (__DEBUG__)
					console.debug('Calc budget', { budget, budgetProduct });

				if (budgetProduct) {
					const geoFencingTotalCpm = this.toFixed((budgetProduct.RemarketingEnabled ? budgetProduct.RemarketingPrice : 0) + budgetProduct.GeoFencingPrice);
					budgetProduct.GeoFencingSum = budgetProduct.GeoFencingEnabled ? this.toFixed(budgetProduct.GeoFencingQuantity * geoFencingTotalCpm / 1000) : 0;

					budgetProduct.Sum = this.toFixed(budgetProduct.TotalSum - budgetProduct.GeoFencingSum);
					budgetProduct.Quantity = this.toFixed(budgetProduct.Sum * 1000 / budgetProduct.RatePrice);
				}

				let totalBudgetSum = 0;
				let totalRecurringProductsSum = 0;
				let totalRecurringFees = this.toFixed(_.sum(budget.Fees.map(f => f.Recurrent ? f.Fee : 0)) || 0);
				let totalOnceFees = this.toFixed(_.sum(budget.Fees.map(f => !f.Recurrent ? f.Fee : 0)) || 0);

				//Calculate products
				this.info.campaignProducts.map(product => {
					const bProd = this.budgetProductItems.find(f => f.product.Oid.toLowerCase() === product.Oid.toLowerCase()).items.find(f => f.budget.Oid === budget.Oid)?.product;					
					const isRecurring = product.type.recurrent !== false;
					const totalSum =  product.type.components.airtime ? product.AirtimeCost : (bProd?.TotalSum || 0);
					totalRecurringProductsSum += isRecurring ? totalSum : 0;
					totalBudgetSum += !isRecurring ? totalSum : 0;
				});

				totalBudgetSum += this.toFixed(totalRecurringProductsSum * budget.NumberOfPeriods) + this.toFixed(totalRecurringFees * budget.NumberOfPeriods) + totalOnceFees;

				budget.TotalRecurring = this.toFixed(totalRecurringProductsSum) + totalRecurringFees;
				budget.Total = totalBudgetSum;

				this.createUpdateCampaignBudget(budget);
			},
			getBudgetItems(product) {
				return this.campaignBudgets.map(f => ({
					product: f.Products?.find(p => p?.Product?.Oid?.toLowerCase() === product.Oid.toLowerCase()) || this.getEmptyProduct({ BudgetId: f.Oid, Product: product }),
					budget: f
				}));
			},
			getEmptyProduct(data) {
				return {
					Oid: null,
					TotalSum: 0,
					Product: null,
					RatingMethod: 0,
					RatePrice: 0,
					Quantity: 0,
					Sum: 0,
					GeoFencingEnabled: true,
					GeoFencingPrice: 0,
					GeoFencingQuantity: 0,
					GeoFencingSum: 0,
					RemarketingEnabled: true,
					RemarketingPrice: 0,
					BudgetId: null,
					...(data || {})
				}
			},
			prepareCampaignBudget(budget) {
				budget.Products = [];
				budget.Fees = [];
				return budget;
			},
			loadCampaignBudgets(force) {
				if (this.campaignBudgets?.length && !force) return Promise.resolve(this.campaignBudgets);
				this.isLoading++;
				return this.$Data.getExternalApi('CampaignBudget/GetCampaignBudgets', `campaignId=${this.campaign?.Oid}`, 999).then(r => {
					const promises = [];
					this.campaignBudgets = _.orderBy(r.Results || [], ['Name']).map(f => {
						this.prepareCampaignBudget(f);
						
						this.isLoading++;
						promises.push(this.loadCampaignBudgetProducts(f).then(() => this.isLoading--));


						this.isLoading++;
						promises.push(this.loadCampaignBudgetFees(f).then(() => this.isLoading--));
						return f;
					});

					if (!this.campaignBudgets.length) {
						this.addNewBudget();
					}

					return (promises.length ? Promise.all(promises) : Promise.resolve([])).then(() => this.campaignBudgets);
				}).finally(() => this.isLoading--);
			},
			loadCampaignBudgetProducts(budget) {				
				if (!budget?.Oid) {
					return Promise.resolve([]);
				}
				return this.$Data.getExternalApi('CampaignBudgetProduct/GetBudgetProducts', `budgetId=${budget?.Oid}`, 999).then(r => {
					this.$set(budget, 'Products', r?.Results || []);
					return r?.Results;
				});
			},
			loadCampaignBudgetFees(budget) {				
				if (!budget?.Oid) {
					return Promise.resolve([]);
				}
				return this.$Data.getExternalApi('CampaignBudgetFee/GetBudgetFees', `budgetId=${budget?.Oid}`, 999).then(r => {
					this.$set(budget, 'Fees', r?.Results || []);
					return r?.Results;
				});
			},
			createUpdateCampaignBudget(budget) {
				return this.$Data.postExternalApi('CampaignBudget/CreateUpdateCampaignBudget', {
					Name: `${this.$tCaption('Budget')} ${this.campaignBudgets.length + 1}`,
					...(budget || {}),
					CampaignId: this.campaign?.Oid
				}).then(r => {
					if (!budget.Oid && r?.Data?.Oid) {
						budget.Oid = r?.Data?.Oid
					}
				});
			},
			removeBudget(budget) {
				this.$confirmMessage({
					title: () => this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to remove budget?'),
					handler: () => {
						const idx = this.campaignBudgets.findIndex(f => f.Oid === budget?.Oid);
						this.campaignBudgets.splice(idx, 1);
						this.$Data.postExternalApi('CampaignBudget/DeleteCampaignBudgets', [budget.Oid]);
					}
				});
			},			
			getCurrencyFormat(budget) {
				return `${this.currencyFormat} ${this.getReccurencyPeriod(budget)}`;
			},
			addNewBudget() {
				const newBudget = this.prepareCampaignBudget({
					Name: `${this.$tCaption('Budget')} ${this.campaignBudgets.length + 1}`,
					CampaignId: this.campaign?.Oid,
					BudgetPeriod: 0,
					NumberOfPeriods: 1,
					Total: 0,
					TotalRecurring: 0,
					TotalRecurringProducts: 0,
					TotalRecurringFee: 0
				});
				this.campaignBudgets.push(newBudget);
				this.calcBudget(newBudget);
			},
			addNewFee(e) {
				e.event.stopPropagation();
				this.fees.push({ name: () => this.$tCaption('New fee'), fee: 0, recurrent: false });
				this.editingFeeIndex = this.fees.length-1;
			},
			toFixed(num) {
				if (isNaN(num)) return 0;
				return Number(parseFloat(num).toFixed(2));
			},
			formatNumber(num, type) {
				return Localization.formatNumber(num, {
					type: type || 'currency',
					precision: 0
				});
			},
			getReccurencyPeriod(budget) {
				if (budget) {
					switch(budget.BudgetPeriod) {						
						case 0:
							return this.$tCaption('weekly');
						case 1:
							return this.$tCaption('monthly');
					}
				}
				return '';
			},
		}
	};
</script>
<style lang='scss'>
	.campaign-budget {
		@import "main/themes/generated/variables.base.scss";

		.clickable {
			cursor: pointer;
		}
		
		.flex-row {
			display: flex;
			flex-direction: row;
			align-items: center;

			> *:not(:first-child) {
				margin-left: 6px;
			}
		}

		.flex-justify {
			flex: 1;
			justify-content: space-between;
		}

		.no-wrap {
			white-space: nowrap;
			flex-wrap: nowrap;
		}

		.horizontal-spacing {
			> * {
				&:not(:first-child) {
					margin-left: 6px;
				}
			}
		}

		.flex-wrap {
			flex-wrap: wrap;
		}

		.flex-col {
			display: flex;
			flex-direction: column;
			justify-content: center;
		}

		.data-value {
			font-size: 12px;
			color: rgba(0,0,0,0.3);
		}

		.data-description {
			color: rgba(0,0,0,0.3);
		}

		.product {
			display: flex;
			flex-direction: row;
			align-items: center;
			font-size: 12px;

			.expander {
				transition: 0.3s;

				&.expanded {
					transform: rotate(90deg);
				}
			}
		}

		.number-edit {
			width: 115px;

			&.bool {
				width: 75px;
			}
		}

		.budget-table {
			font-size: 12px;

			.budget-td {
				vertical-align: top;
			}

			td {
				padding: 6px;
			}

			.details {
				background: #dedede;
				td {
					padding: 5px 10px 5px 10px;
				}
			}

			.budget-td:nth-child(even) {
				background-color: #e8e8e8;
			}

			.budget-td:nth-child(odd) {
				background-color: #dedede;
			}

			.total-row {
				background: $base-accent;
				color: #fff;

				> td {
					border-bottom: 1px solid rgba(0,0,0,0.1);
				}

				.budget-td {
					&:nth-child(even) {
						background: $base-accent;
					}

					&:nth-child(odd) {
						background: $base-accent-hover;
					}
				}

				.budget-total {
					display: flex;
					align-items: baseline;

					.value {
						font-size: 16px;
						position: relative;

						.description {
							position: absolute;
							font-size: 7px;
							margin-left: 6px;
							right: 0px;
							bottom: -5px;
							color: rgba(0,0,0,0.5);
						}
					}

					.description {
						font-size: 12px;
						margin-left: 6px;
					}
				}
			}

			.budget-totals {
				td {
					background: #e3e3e3 !important;
					color: #333 !important;
					padding-left: 22px;
				}
			}

			.fees {
				td {
					padding-left: 22px !important;

					&:first-child {
						background: #e8e8e8 !important;
					}
				}
			}

			.grand-total-row {
				td:last-child, {
					border-bottom-right-radius: 8px;
				}

				td:first-child {
					border-bottom-left-radius: 8px;
				}
			}

			.details {
				opacity: 1;

				&:not(.invisible) {
					transition: 0.4s;
				}

				td:first-child {
					padding-left: 46px;
				}

				.highlight {
					color: #53c5b0;
					font-size: 15px;
					font-weight: 600;
				}

				> td {
					border-color: #3333330a;
					border-bottom-width: 1px;
					border-bottom-style: solid;
				}
			}

			.header-row {
				font-size: 14px;
				font-weight: 100;

				.budget-td:nth-child(2) {
					border-top-left-radius: 8px;
				}

				.budget-td:last-child {
					border-top-right-radius: 8px;
				}
			}

			.rounded-left {
				border-top-left-radius: 8px;
				background: #dedede;
			}
		}

		.center-aligned-number-box .dx-texteditor-input {
			text-align: center;
		}
		
		.dx-texteditor.dx-editor-underlined{
			background: #00000008 !important;
			border-radius: 4px !important;
			padding: 0px 6px !important;

			&::after {
				border-bottom: none !important;
			}

			.dx-texteditor-input {
				font-size: 12px !important;
			}			
		}

		.simplified-editors {
			.dx-texteditor.dx-editor-underlined {
				background: none !important;
			}
		}

		.colored-editors {
			.dx-texteditor.dx-editor-underlined {
				background: #00000008 !important;
			}
		}
	}
</style>
